import { z } from 'zod';

/**
 * Runtime configuration, validated once at module load.
 *
 * Vite inlines `import.meta.env.VITE_*` at build time, so a missing variable
 * produces `undefined` in the bundle rather than a build error. Validating here
 * turns that into a loud failure on the first page load instead of a
 * `baseURL: undefined` request that 404s against the admin's own origin.
 */
const schema = z.object({
  VITE_API_BASE_URL: z.url({ error: 'VITE_API_BASE_URL must be an absolute URL.' }),
  VITE_APP_URL: z.url({ error: 'VITE_APP_URL must be an absolute URL.' }),
  VITE_SITE_URL: z.url({ error: 'VITE_SITE_URL must be an absolute URL.' }),
  VITE_DEFAULT_LOCALE: z
    .string()
    .regex(/^[a-z]{2}(-[A-Z]{2})?$/, 'VITE_DEFAULT_LOCALE must be a BCP-47 tag such as `en`.')
    .default('en'),
  VITE_REQUEST_TIMEOUT_MS: z.coerce.number().int().positive().max(120_000).default(20_000),
  // Import runs are synchronous and can exceed the default HTTP timeout on a full corpus.
  VITE_IMPORT_TIMEOUT_MS: z.coerce.number().int().positive().max(600_000).default(300_000),
});

const raw = import.meta.env as unknown as Record<string, string | undefined>;

const parsed = schema.safeParse({
  VITE_API_BASE_URL: raw['VITE_API_BASE_URL'],
  VITE_APP_URL: raw['VITE_APP_URL'],
  VITE_SITE_URL: raw['VITE_SITE_URL'],
  VITE_DEFAULT_LOCALE: raw['VITE_DEFAULT_LOCALE'],
  VITE_REQUEST_TIMEOUT_MS: raw['VITE_REQUEST_TIMEOUT_MS'],
  VITE_IMPORT_TIMEOUT_MS: raw['VITE_IMPORT_TIMEOUT_MS'],
});

if (!parsed.success) {
  const detail = parsed.error.issues
    .map((issue) => `  ${issue.path.join('.') || '(root)'}: ${issue.message}`)
    .join('\n');

  throw new Error(`Invalid admin environment configuration:\n${detail}\n\nSee .env.example.`);
}

const value = parsed.data;

export const env = {
  /** Laravel API origin, without a trailing slash. */
  apiBaseUrl: value.VITE_API_BASE_URL.replace(/\/+$/, ''),
  /** Where the admin itself is served. Used for absolute callback URLs. */
  appUrl: value.VITE_APP_URL.replace(/\/+$/, ''),
  /** Public marketing site, used to build "view on site" and preview links. */
  siteUrl: value.VITE_SITE_URL.replace(/\/+$/, ''),
  defaultLocale: value.VITE_DEFAULT_LOCALE,
  requestTimeoutMs: value.VITE_REQUEST_TIMEOUT_MS,
  importTimeoutMs: value.VITE_IMPORT_TIMEOUT_MS,
  isDev: import.meta.env.DEV,
} as const;

export type Env = typeof env;
